import React from 'react';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import UarDropdowns from './components/UarDropdowns';
import { fetchUarAgeing, fetchUarDivisions, fetchUarMrDetails, UARAgeingRow, UARDivision, UARMrDetailRow } from './api';
import { fetchAraYears, fetchSubordinates, Subordinate } from 'pages/ara/api';
import UarTable from './components/UarTable';
import UarMrDetailsTable from './components/UarMrDetailsTable';

const fyOrder = (m: number) => (m >= 4 ? m - 3 : m + 9);

const UarPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [years, setYears] = React.useState<string[]>([]);
    const [yearMonthDayMap, setYearMonthDayMap] = React.useState<Record<string, Record<number, number[]>>>({});
    const [availableMonths, setAvailableMonths] = React.useState<number[]>([]);
    const [availableDays, setAvailableDays] = React.useState<number[]>([]);
    const [year, setYear] = React.useState('');
    const [month, setMonth] = React.useState<number | null>(null);
    const [day, setDay] = React.useState<number | null>(null);

    const [divisions, setDivisions] = React.useState<UARDivision[]>([]);
    const [totalUar, setTotalUar] = React.useState<number>(0);
    const [unallocatedUar, setUnallocatedUar] = React.useState<number>(0);
    const [loadingDivisions, setLoadingDivisions] = React.useState<boolean>(true);

    const [selectedDivision, setSelectedDivision] = React.useState<string | null>(null);
    const [collectionResp, setCollectionResp] = React.useState<'allocated' | 'unallocated' | null>(null);

    const [selectedUser, setSelectedUser] = React.useState<string | null>(null);
    const [selectedCustomer, setSelectedCustomer] = React.useState<{ code: string; name: string; } | null>(null);
    const [users, setUsers] = React.useState<Subordinate[]>([]);
    const [loadingUsers, setLoadingUsers] = React.useState<boolean>(false);
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');

    const [tableData, setTableData] = React.useState<UARAgeingRow[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState('');

    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [totalItems, setTotalItems] = React.useState<number>(0);
    const [sortBy, setSortBy] = React.useState<keyof UARAgeingRow>('uar');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
    const [customerSearch, setCustomerSearch] = React.useState<string>('');
    const [debouncedCustomerSearch, setDebouncedCustomerSearch] = React.useState<string>('');

    const [mrDetails, setMrDetails] = React.useState<UARMrDetailRow[]>([]);
    const [mrLoading, setMrLoading] = React.useState(false);

    const [mrPage, setMrPage] = React.useState<number>(1);
    const [mrPageSize, setMrPageSize] = React.useState<number>(20);
    const [mrTotalItems, setMrTotalItems] = React.useState<number>(0);
    const [mrSortBy, setMrSortBy] = React.useState<'reference_no' | 'utr_no' | 'utr_date' | 'utr_amt' | 'mr_date' | 'overdue_days' | 'transaction_amt' | 'mr_amount'>('mr_date');
    const [mrSortOrder, setMrSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const hasLoadedOnce = React.useRef(false);


    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports/pnl' },
            { label: 'Unapplied Receipts' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedCustomerSearch(customerSearch.trim() || '');
            setPage(1);
        }, 400);

        return () => clearTimeout(t);
    }, [customerSearch]);

    React.useEffect(() => {
        const loadYears = async () => {
            try {
                const resp = await fetchAraYears();

                const map: Record<string, Record<number, number[]>> = {};
                const yearList: string[] = [];

                resp.years.forEach(({ year, months }) => {
                    map[year] = months;
                    yearList.push(year);
                });

                setYearMonthDayMap(map);
                setYears(yearList);

                if (yearList.length) {
                    const defaultYear = yearList[0];
                    const monthMap = map[defaultYear] ?? {};

                    const sortedMonths = Object.keys(monthMap)
                        .map(Number)
                        .sort((a, b) => fyOrder(b) - fyOrder(a));

                    if (sortedMonths.length) {
                        const latestMonth = sortedMonths[0];
                        const days = map[defaultYear][latestMonth] ?? [];
                        const sortedDays = [...days].sort((a, b) => b - a);

                        setYear(defaultYear);
                        setAvailableMonths(sortedMonths);
                        setMonth(latestMonth);
                        setAvailableDays(sortedDays);
                        setDay(sortedDays[0] ?? null);
                    }
                }
            } catch {
                setYearMonthDayMap({});
                setYears([]);
            }
        };
        loadYears();
    }, []);

    React.useEffect(() => {
        if (collectionResp !== 'allocated') {
            setUsers([]);
            setSelectedUser(null);
            return;
        }

        setLoadingUsers(true);
        fetchSubordinates(
            selectedDivision && selectedDivision !== 'unallocated'
                ? selectedDivision
                : undefined
        )
            .then((res) => {
                setUsers(res.items);
            })
            .catch(() => {
                setUsers([]);
            })
            .finally(() => {
                setLoadingUsers(false);
            });
    }, [collectionResp, selectedDivision]);

    React.useEffect(() => {
        if (!year || !month || !day) return;

        setLoadingDivisions(true);
        fetchUarDivisions(year, month, day)
            .then((res) => {
                setDivisions(res.divisions);
                setTotalUar(res.total_uar);
                setUnallocatedUar(res.unallocated_uar);

                // reset filters
                setSelectedDivision(null);
                setCollectionResp(null);
                setSelectedUser(null);
            })
            .finally(() => {
                setLoadingDivisions(false);
            });
    }, [year, month, day]);

    React.useEffect(() => {
        if (!year || !month || !day) return;

        setLoading(true);
        setError('');

        fetchUarAgeing({
            year,
            month,
            day,
            division: selectedDivision ?? undefined,
            ecode: selectedUser ?? undefined,
            collection_resp: collectionResp ?? undefined,
            search: debouncedCustomerSearch || undefined,
            page,
            per_page: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
        })
            .then((res) => {
                setTableData(res.rows);
                setTotalItems(res.total_items);
            })
            .catch(() => {
                setError('Failed to fetch UAR ageing data');
            })
            .finally(() => {
                setLoading(false);
                hasLoadedOnce.current = true;
            });
    }, [year, month, day, selectedDivision, selectedUser, collectionResp, debouncedCustomerSearch, page, pageSize, sortBy, sortOrder]);


    React.useEffect(() => {
        if (!year || !month || !day || !selectedCustomer) return;

        const loadMrDetails = async () => {
            setMrLoading(true);
            try {
                const res = await fetchUarMrDetails({
                    year,
                    month,
                    day,
                    customer_code: selectedCustomer.code,
                    page: mrPage,
                    per_page: mrPageSize,
                    sort_by: mrSortBy,
                    sort_order: mrSortOrder,
                });

                setMrDetails(res.rows);
                setMrTotalItems(res.total_items);
            } finally {
                setMrLoading(false);
            }
        };
        loadMrDetails();
    }, [year, month, day, selectedCustomer, mrPage, mrPageSize, mrSortBy, mrSortOrder]);

    const handleSortChange = (col: keyof UARAgeingRow) => {
        setPage(1);
        if (sortBy === col) {
            setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(col);
            setSortOrder('asc');
        }
    };

    const handleMrSortChange = (col: 'reference_no' | 'utr_no' | 'utr_date' | 'utr_amt' | 'mr_date' | 'overdue_days' | 'transaction_amt' | 'mr_amount') => {
        setMrPage(1);
        if (mrSortBy === col) {
            setMrSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
        } else {
            setMrSortBy(col);
            setMrSortOrder('asc');
        }
    };

    const handleYearChange = (y: string) => {
        setYear(y);

        const monthMap = yearMonthDayMap[y] ?? {};
        const months = Object.keys(monthMap).map(Number).sort((a, b) => fyOrder(b) - fyOrder(a));

        setAvailableMonths(months);

        if (months.length) {
            const firstMonth = months[0];
            const days = monthMap[firstMonth] ?? [];
            const sortedDays = [...days].sort((a, b) => b - a);

            setMonth(firstMonth);
            setAvailableDays(sortedDays);
            setDay(sortedDays[0] ?? null);
        } else {
            setMonth(null);
            setAvailableDays([]);
            setDay(null);
        }

        setPage(1);
    };


    const handleMonthChange = (m: number) => {
        setMonth(m);

        const days = yearMonthDayMap[year]?.[m] ?? [];
        const sortedDays = [...days].sort((a, b) => b - a);

        setAvailableDays(sortedDays);
        setDay(sortedDays[0] ?? null);
        setPage(1);
    };

    const handleDivisionSelect = (div: string | null) => {
        setSelectedDivision(div);
        setSelectedUser(null);
        setPage(1);
    };

    const handleCollectionRespChange = (val: 'allocated' | 'unallocated' | null) => {
        setCollectionResp(val);
        if (val !== 'allocated') setSelectedUser(null);

        setPage(1);
    };

    const handleConversionChange = (val: 'INR' | 'Lakhs' | 'Crores') => {
        setConversionType(val);
        setPage(1);
    };

    const handleUserChange = (user: string | null) => {
        setSelectedUser(user);
        setPage(1);
    };

    const handleCustomerClick = (code: string, name: string) => {
        setSelectedCustomer({ code, name });
        setMrPage(1);
    };

    return (
        <div className="h-full flex flex-col overflow-hidden px-6 py-4">
            {selectedCustomer ? (
                <div className="flex flex-col h-full min-h-0">
                    <UarMrDetailsTable
                        data={mrDetails}
                        loading={mrLoading}
                        customerName={selectedCustomer.name}
                        page={mrPage}
                        pageSize={mrPageSize}
                        totalItems={mrTotalItems}
                        sortBy={mrSortBy}
                        sortOrder={mrSortOrder}
                        onSortChange={handleMrSortChange}
                        onPageChange={setMrPage}
                        onPageSizeChange={(s) => {
                            setMrPage(1);
                            setMrPageSize(s);
                        }}
                        onBack={() => setSelectedCustomer(null)}
                        conversionType={conversionType}
                        onConversionChange={handleConversionChange}
                    />
                </div>
            ) : (
                <>
                    <UarDropdowns
                        years={years}
                        year={year}
                        month={month}
                        day={day}
                        months={availableMonths}
                        days={availableDays}
                        divisions={divisions}
                        totalUar={totalUar}
                        unallocatedUar={unallocatedUar}
                        loadingDivisions={loadingDivisions}
                        selectedDivision={selectedDivision}
                        collectionResp={collectionResp}
                        selectedUser={selectedUser}
                        users={users}
                        loadingUsers={loadingUsers}
                        conversionType={conversionType}
                        onYearChange={handleYearChange}
                        onMonthChange={handleMonthChange}
                        onDayChange={setDay}  
                        onDivisionSelect={handleDivisionSelect}
                        onCollectionRespChange={handleCollectionRespChange}
                        onConversionChange={handleConversionChange}
                        onUserChange={handleUserChange}
                        customerSearch={customerSearch}
                        onCustomerSearchChange={setCustomerSearch}
                    />

                    <div className="flex-1 min-h-0 mt-6">
                        <UarTable
                            data={tableData}
                            loading={loading}
                            page={page}
                            pageSize={pageSize}
                            totalItems={totalItems}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSortChange={handleSortChange}
                            onPageChange={setPage}
                            onPageSizeChange={(s) => {
                                setPage(1);
                                setPageSize(s);
                            }}
                            hasLoadedOnce={hasLoadedOnce.current}
                            hasFiltersSelected={Boolean(year && month)}
                            error={error}
                            conversionType={conversionType}
                            onCustomerClick={handleCustomerClick}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default UarPage;
